/**
 * @file 云钱包赎回模块相关hook
 */
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from 'react';
import {
  UnStakeOption,
  UnstakeType,
  Process,
  UnstakeCoinInfoStore,
  DelayRedeemInfo,
} from '@/types/unstake';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { getUnstakeEstimateToken } from '@/services/unstake';
import BigNumber from 'bignumber.js';
import { NeedSlippageType } from '@/types/pool';
import { useCurrentChainAddress } from '@/hooks/wallet';
import dayjs from 'dayjs';
import { formatNumber } from '@web3/base-utils';

export const useSolLoss = (
  selectCoinList: string[],
  unstakeCoinAmountStore: UnstakeCoinInfoStore
) => {
  const { poolInfo } = usePoolInfoContext();
  const [isSolLoss, setIsSolLoss] = useState(false);

  useEffect(() => {
    const isLoss = unstakeCoinAmountStore[selectCoinList[0]]?.isLoss ?? false;
    setIsSolLoss(poolInfo.chainCode === 'SOL' && isLoss);
  }, [poolInfo, unstakeCoinAmountStore, selectCoinList]);

  return {
    isSolLoss,
  };
};
/**
 * 滑点相关hook
 */
export const useSlippage = (
  selectCoinList: string[],
  unstakeCoinAmountStore: UnstakeCoinInfoStore
) => {
  const { poolInfo } = usePoolInfoContext();

  // 普通滑点
  const [slippage, setSlippage] = useState(
    // 如果可以让用户滑点，默认值为0.5，否则为空并且不让用户设置滑点
    poolInfo.needSlippage === NeedSlippageType.Need ? '0.5' : ''
  );

  // 固定滑点，由后端提供
  const [solidSlippage, setSolidSlippage] = useState('');

  const onChangeSlippage = (v: string) => {
    setSlippage(v);
  };

  useEffect(() => {
    if (!selectCoinList.length) return;
    const unstakeCoinInfo = unstakeCoinAmountStore[selectCoinList[0]] || {};
    // 设置固定滑点
    setSolidSlippage(unstakeCoinInfo?.slippage || '');

    // 当前选择币种切换或者切换多币单币类型则更新固定滑点，unstakeCoinAmountStore如果更新说明切换了类型
  }, [selectCoinList[0], unstakeCoinAmountStore]);

  // 滑点优先级 固定 > 普通
  const finalSlippage = solidSlippage || slippage;

  // 如果滑点大于1%则标注受损
  const slippageIsLost = BigNumber(finalSlippage).gte(1);

  return {
    slippage: finalSlippage,
    slippageIsLost,
    hasSolidSlippage: !!solidSlippage,
    onChangeSlippage,
  };
};

/**
 * 赎回金额相关hook
 */
export const useUnstakeAmount = (option: Partial<UnStakeOption>) => {
  const { accountInfo } = option as UnStakeOption;
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [process, setProcess] = useState(Process.None);

  const onChangeUnstakeAmount = (v: string) => {
    setUnstakeAmount(v);
  };

  const onChangeProcess = (step: Process) => {
    setProcess(step);
  };

  useEffect(() => {
    setUnstakeAmount(
      formatNumber(accountInfo?.lpTokens, {
        kmb: true,
        decimal: 8,
        smallNumberUseValidDigits: false,
        collapseZero: false,
        roundMode: BigNumber.ROUND_DOWN,
      })
    );
  }, []);

  return {
    unstakeAmount,
    process,
    onChangeProcess,
    onChangeUnstakeAmount,
  };
};

/**
 * 选择赎回币种相关hook
 */
export const useSelectCoinList = (option: Partial<UnStakeOption>) => {
  const { unstakeAmount, showType } = option as UnStakeOption;
  const { poolInfo } = usePoolInfoContext();
  const [unstakeType, setUnstakeType] = useState(
    poolInfo.multipleCoins?.length ? UnstakeType.Multiple : UnstakeType.Single
  );
  const [delayRedeemInfo, setDelayRedeemInfo] = useState<DelayRedeemInfo>(
    {} as DelayRedeemInfo
  );
  const [selectCoinList, setSelectCoinList] = useState(
    poolInfo.multipleCoins?.length
      ? poolInfo.multipleCoins.map((item) => item.contractAddress)
      : [poolInfo.singleCoins[0].contractAddress]
  );
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);
  const [unstakeCoinAmountStore, setUnstakeCoinAmountStore] =
    useState<UnstakeCoinInfoStore>({});
  const timer = useRef<NodeJS.Timeout>();

  // 获取赎回币种相关数量
  const fetchCoinAmount = useCallback(async () => {
    const { success, result } = await getUnstakeEstimateToken({
      chainId: +poolInfo.chainId,
      poolId: poolInfo.id,
      lpTokens: unstakeAmount,
      type: unstakeType,
      unstakeTokens: (unstakeType === UnstakeType.Single
        ? poolInfo.singleCoins
        : poolInfo.multipleCoins
      ).map((item) => item.contractAddress),
      slippage: '0.5',
      walletAddress,
      unstakeType: showType,
    });
    if (success) {
      const newTokenAmountStore: UnstakeCoinInfoStore = {};

      result.tokenAmounts.forEach((coinAmountInfo) => {
        newTokenAmountStore[`${coinAmountInfo.contractAddress}`] = {
          amount: coinAmountInfo.amount,
          slippage: coinAmountInfo.slippage,
          router: coinAmountInfo?.router ?? '',
          isLoss: coinAmountInfo?.isLoss ?? false,
        };
      });

      const finalTime =
        dayjs().utc().valueOf() +
        (result?.delayInfo?.currentEpochRemain ?? 0) * 1000;

      setDelayRedeemInfo({ ...result.delayInfo, finalTime });

      setUnstakeCoinAmountStore(newTokenAmountStore);
    }
  }, [poolInfo, unstakeAmount, unstakeType]);

  const onChangeUnstakeType = (v: UnstakeType) => {
    // 切换赎回币种tab后需要重新设置当前选中的币
    if (v === UnstakeType.Single) {
      setSelectCoinList([poolInfo.singleCoins?.[0]?.contractAddress]);
    }

    if (v === UnstakeType.Multiple) {
      setSelectCoinList(
        poolInfo.multipleCoins.map((item) => item.contractAddress)
      );
    }
    setUnstakeType(v);
  };

  const onChangeSelectCoinList = (v: string[]) => {
    setSelectCoinList(v);
  };

  useLayoutEffect(() => {
    setUnstakeCoinAmountStore({});
  }, [fetchCoinAmount]);

  useEffect(() => {
    timer.current = setTimeout(() => {
      fetchCoinAmount();
    }, 200);
    return () => {
      clearTimeout(timer.current);
    };
  }, [fetchCoinAmount]);

  return {
    delayRedeemInfo,
    selectCoinList,
    unstakeType,
    unstakeCoinAmountStore,
    onChangeUnstakeType,
    onChangeSelectCoinList,
  };
};
